import { Link } from "react-router-dom";
import { ArrowUpRight, Check, MessageCircle } from "lucide-react";
import Reveal from "@/components/ui/Reveal";
import FAQ from "@/components/ui/FAQ";
import OrderFlow from "@/components/ui/OrderFlow";
import usePageMeta from "@/hooks/usePageMeta";
import { CORE_SERVICES, INDUSTRIES, wa } from "@/constants/content";

export default function Services() {
  usePageMeta({
    title: "Services · 64+ Offers from ₹349",
    description:
      "5 core services + monthly systems. 64+ active offers from ₹349 to ₹29,999, plus 7 industry-specific monthly packs from ₹4,999 to ₹24,999 per month.",
    path: "/services",
  });

  return (
    <div data-testid="page-services" className="pt-28 md:pt-36">
      <section className="px-6 md:px-12 max-w-[1440px] mx-auto">
        <p className="text-[11px] uppercase tracking-[0.22em] text-[#e03c31] font-bold mb-5">
          ✱ 2026 Service Catalog
        </p>
        <h1 className="h1-display text-white text-[8vw] md:text-[4.5vw] max-w-[22ch]">
          64+ active services across{" "}
          <span className="text-[#e03c31]">6 lanes.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-sm md:text-[15px] text-zinc-300 leading-relaxed">
          One studio, five core service lanes plus a recurring monthly engine. Pick a single
          creative, an identity system, an ads support item, or subscribe to an industry pack —
          every output follows the same brief-first, copyright-safe workflow.
        </p>
      </section>

      {/* 6 LANES grid */}
      <section className="px-6 md:px-12 max-w-[1440px] mx-auto mt-16 md:mt-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {CORE_SERVICES.map((l, i) => (
            <Reveal key={l.n} delay={i * 60}>
              <Link
                to={l.to}
                data-testid={`service-lane-${l.n}`}
                className="relative block bg-[#0a0a0c] border border-white/5 rounded-3xl p-7 md:p-8 card-lift card-glow h-full group"
              >
                <div className="flex items-start justify-between mb-6">
                  <span className="font-cabinet text-[#e03c31] text-xs tracking-widest">{l.n}</span>
                  <ArrowUpRight
                    size={18}
                    className="text-zinc-500 group-hover:text-[#e03c31] arrow-nudge"
                  />
                </div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-2.5">
                  {l.count}
                </p>
                <h3 className="font-cabinet text-lg md:text-xl font-bold mb-2.5 leading-snug">
                  {l.title}
                </h3>
                <p className="text-zinc-400 text-[13px] leading-relaxed mb-5">{l.blurb}</p>
                <div className="text-xs font-bold text-white pt-3 border-t border-white/5">
                  {l.price}
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* INDUSTRY PACKS */}
      <section className="px-6 md:px-12 max-w-[1440px] mx-auto mt-24 md:mt-32">
        <p className="text-[11px] uppercase tracking-[0.22em] text-[#e03c31] font-bold mb-4">
          ✱ 7 industry packs
        </p>
        <h2 className="h2-section text-2xl md:text-4xl mb-3 max-w-[24ch]">
          Industry-specific monthly packs and branding kits.
        </h2>
        <p className="text-zinc-400 max-w-2xl mb-10 text-sm md:text-[15px]">
          The catalog includes universal monthly plans plus vertical packs for clinic, startup,
          restaurant, salon, retail, education, and construction businesses that need more than
          one isolated design request.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {INDUSTRIES.map((ind, i) => (
            <Reveal key={ind.name} delay={i * 40}>
              <div
                data-testid={`industry-${i + 1}`}
                className="bg-[#0a0a0c] border border-white/5 rounded-2xl p-6 card-lift card-glow h-full flex flex-col"
              >
                <div className="flex items-start justify-between gap-4 mb-2 flex-wrap">
                  <h3 className="font-cabinet text-base md:text-lg font-bold leading-snug">{ind.name}</h3>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-[#e03c31] font-bold whitespace-nowrap">
                    {ind.range}
                  </span>
                </div>
                <p className="text-zinc-400 text-[13px] leading-relaxed flex-1">{ind.body}</p>
                <Link
                  to="/services/monthly-systems"
                  className="mt-4 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-white hover:text-[#e03c31]"
                  data-testid={`industry-cta-${i + 1}`}
                >
                  See monthly options <ArrowUpRight size={12} className="arrow-nudge" />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ORDER FLOW */}
      <OrderFlow />

      {/* BEFORE YOU SUBMIT - real note */}
      <section className="px-6 md:px-12 max-w-[1440px] mx-auto mt-20 md:mt-28">
        <div className="rounded-[2rem] glass p-7 md:p-10">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#e03c31] font-bold mb-3">
            ✱ Before you submit
          </p>
          <h3 className="font-cabinet text-lg md:text-2xl font-bold mb-5">
            Read these notes — they save a back-and-forth.
          </h3>
          <ul className="space-y-2.5 text-zinc-300 text-sm md:text-[15px]">
            <li className="flex items-start gap-3">
              <Check size={16} className="text-[#e03c31] mt-1 shrink-0" />
              Choose the closest service only if you already know it — otherwise pick &ldquo;Need a recommendation&rdquo;.
            </li>
            <li className="flex items-start gap-3">
              <Check size={16} className="text-[#e03c31] mt-1 shrink-0" />
              Mobile number stays mandatory for a reliable callback.
            </li>
            <li className="flex items-start gap-3">
              <Check size={16} className="text-[#e03c31] mt-1 shrink-0" />
              Ads setup and ads management pricing do not include ad spend — the media budget is
              always extra.
            </li>
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <FAQ />

      {/* CTA */}
      <section className="px-6 md:px-12 max-w-[1440px] mx-auto mt-20 md:mt-28">
        <div className="rounded-[2rem] glass p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <h2 className="h2-section text-2xl md:text-4xl max-w-xl">
            Not sure which lane fits? We&apos;ll match the right one in 1 reply.
          </h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={wa("package")}
              target="_blank"
              rel="noreferrer"
              className="btn-magnetic"
              data-testid="services-whatsapp-cta"
            >
              <MessageCircle size={14} /> Ask for the right package
            </a>
            <Link to="/contact" className="btn-magnetic btn-ghost" data-testid="services-cta-contact">
              Open Contact Us <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
